import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Spinner } from './ui';
import Tooltip from './Tooltip';

export interface EventDef {
  key: string;
  label: string;
  desc: string;
}

export interface NotifPref {
  event_type: string;
  channel: string;
  email_override: string | null;
}

interface Props {
  events: EventDef[];
  prefs: NotifPref[];
  onChange: (eventType: string, field: 'channel' | 'email_override', value: string) => void;
  smsIneligible: boolean;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
  error: string;
  showSmsDisclosure?: boolean;
  onSmsSettingsClick?: () => void;
}

const CHANNELS = ['email', 'sms', 'both', 'none'] as const;
type Channel = typeof CHANNELS[number];

const CHANNEL_LABELS: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  both: 'Both',
  none: 'None',
};

export default function NotificationPrefsPanel({
  events, prefs, onChange, smsIneligible, onSave, saving, saved, error,
  showSmsDisclosure = false, onSmsSettingsClick,
}: Props) {
  const getPref = (key: string): NotifPref =>
    prefs.find(p => p.event_type === key) ?? { event_type: key, channel: 'email', email_override: null };

  const hasSmsSelected = prefs.some(p => p.channel === 'sms' || p.channel === 'both');

  return (
    <div className="space-y-3">
      {showSmsDisclosure && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-2 text-sm text-gray-400">
          <p><span className="font-medium text-gray-300">Consent Statement:</span> By entering your phone number on your profile and choosing SMS as a notification delivery preference, you consent to receive SMS messages from CoachSight about coaching updates and notifications.</p>
          <p><span className="font-medium text-gray-300">Message Frequency:</span> Up to 50 messages per month depending on your activity and preferences. No marketing or promotional messages.</p>
          <p><span className="font-medium text-gray-300">Rates:</span> Message and data rates may apply.</p>
          <p><span className="font-medium text-gray-300">Opt-Out:</span> Reply STOP to unsubscribe at any time.</p>
          <p>
            <span className="font-medium text-gray-300">Terms &amp; Conditions and Privacy Policy:</span>{' '}
            See our{' '}
            <a href="https://www.coachsightecr.com/terms#6-sms-messaging-program" className="text-primary-400 hover:underline" target="_blank" rel="noreferrer">Terms &amp; Conditions</a>
            {' '}and{' '}
            <a href="https://www.coachsightecr.com/privacy#3-sms-messaging-program" className="text-primary-400 hover:underline" target="_blank" rel="noreferrer">Privacy Policy</a>
            {' '}for more information.
          </p>
        </div>
      )}

      {hasSmsSelected && smsIneligible && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">
          <span className="text-amber-400 text-base leading-none mt-0.5">&#9888;</span>
          <p className="text-xs text-amber-300">
            SMS requires a verified US or Canada phone number.
            {onSmsSettingsClick && (
              <> <button className="underline" onClick={onSmsSettingsClick}>Update profile</button> to verify.</>
            )}
          </p>
        </div>
      )}

      {events.map(ev => {
        const p = getPref(ev.key);
        const showEmail = p.channel === 'email' || p.channel === 'both';
        return (
          <div key={ev.key} className="bg-dark-card border border-dark-border rounded-xl p-4 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-white">{ev.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{ev.desc}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {CHANNELS.map(ch => {
                  const isSmsOption = ch === 'sms' || ch === 'both';
                  const isDisabled = isSmsOption && smsIneligible;
                  const isActive = p.channel === ch && !isDisabled;
                  const btn = (
                    <button
                      key={ch}
                      onClick={() => !isDisabled && onChange(ev.key, 'channel', ch)}
                      disabled={isDisabled}
                      className={`px-2.5 py-1 text-xs rounded-lg border transition-colors font-medium ${
                        isActive
                          ? ch === 'none'
                            ? 'bg-gray-700 border-gray-600 text-gray-300'
                            : 'bg-primary-600 border-primary-600 text-white'
                          : isDisabled
                          ? 'border-dark-border text-gray-600 cursor-not-allowed'
                          : 'border-dark-border text-gray-400 hover:border-primary-500 hover:text-white'
                      }`}
                    >
                      {CHANNEL_LABELS[ch]}
                    </button>
                  );
                  return isDisabled
                    ? <Tooltip key={ch} text="Requires a verified US or Canada phone number">{btn}</Tooltip>
                    : <React.Fragment key={ch}>{btn}</React.Fragment>;
                })}
              </div>
            </div>
            {showEmail && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email address(es)</label>
                <input
                  type="text"
                  value={p.email_override ?? ''}
                  onChange={e => onChange(ev.key, 'email_override', e.target.value)}
                  placeholder="Leave blank to use account email"
                  className="w-full bg-dark-canvas border border-dark-border text-gray-200 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-500 placeholder-dark-muted"
                />
              </div>
            )}
          </div>
        );
      })}

      {error && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {saving ? <Spinner size="sm" /> : 'Save preferences'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-green-400">
            <CheckCircleIcon className="w-4 h-4" /> Saved.
          </span>
        )}
      </div>
    </div>
  );
}
